/**
 * compressor.js — client-side image downscale before upload.
 *
 * Phone photos of certificates are typically 3-8MB (3000-4000px). The vision
 * model caps effective input around 2000px long edge; downscaling on-device
 * to ~1600px JPEG cuts upload size by ~90% and speeds extraction, with no
 * measurable OCR accuracy loss on printed documents.
 *
 * PDFs are passed through untouched — the Space rasterizes them server-side
 * with PyMuPDF at 2x zoom, which is higher quality than browser rasterization.
 */

const MAX_LONG_EDGE = 1600;
const JPEG_QUALITY = 0.82;

export function isImage(file) {
  return /^image\//.test(file.type);
}

export function isPdf(file) {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
}

/**
 * Downscale an image file if it exceeds MAX_LONG_EDGE. Returns the original
 * file when already small enough or on any processing failure (fail-open).
 * @returns {Promise<File>}
 */
export async function compressImage(file) {
  if (!isImage(file)) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const longest = Math.max(bitmap.width, bitmap.height);
    if (longest <= MAX_LONG_EDGE) {
      bitmap.close?.();
      return file;
    }
    const scale = MAX_LONG_EDGE / longest;
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', JPEG_QUALITY);
    });
    const name = (file.name || 'photo.jpg').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (_e) {
    return file; // fail-open: send the original
  }
}
